import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { apiFetch } from "../../utils/api";
import "./AdminShopOrders.css";

const fmt = (n) =>
  "R " +
  Number(n ?? 0).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function fmtDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-ZA", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function fmtDateTime(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-ZA", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* ─────────────────────────────────
   Constants
   ───────────────────────────────── */
const ORDER_STATUSES = [
  { value: "pending",    label: "Pending" },
  { value: "confirmed",  label: "Confirmed" },
  { value: "processing", label: "Processing" },
  { value: "shipped",    label: "Shipped" },
  { value: "delivered",  label: "Delivered" },
  { value: "cancelled",  label: "Cancelled" },
  { value: "refunded",   label: "Refunded" },
];

const PAYMENT_STATUSES = [
  { value: "unpaid",   label: "Unpaid" },
  { value: "paid",     label: "Paid" },
  { value: "partial",  label: "Partial" },
  { value: "refunded", label: "Refunded" },
];

/* ─────────────────────────────────
   Status badges
   ───────────────────────────────── */
function OrderStatusBadge({ status }) {
  return (
    <span className={`aso-status aso-status--${status || "pending"}`}>
      {ORDER_STATUSES.find((s) => s.value === status)?.label || status || "Pending"}
    </span>
  );
}

function PaymentBadge({ status }) {
  return (
    <span className={`aso-payment aso-payment--${status || "unpaid"}`}>
      {PAYMENT_STATUSES.find((s) => s.value === status)?.label || status || "Unpaid"}
    </span>
  );
}

/* ─────────────────────────────────
   Dot menu
   ───────────────────────────────── */
function DotMenu({ onView, onUpdateStatus, onConfirmPayment, onDelete, isPaid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="aso-dot-menu" ref={ref}>
      <button
        className="aso-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="aso-dot-menu__dropdown">
          <button onClick={() => { onView(); setOpen(false); }}>
            <span className="aso-dot-menu__icon">👁</span> View Details
          </button>
          <button onClick={() => { onUpdateStatus(); setOpen(false); }}>
            <span className="aso-dot-menu__icon">✏</span> Update Status
          </button>
          {!isPaid && (
            <button onClick={() => { onConfirmPayment(); setOpen(false); }}>
              <span className="aso-dot-menu__icon">✅</span> Confirm Payment
            </button>
          )}
          <button
            className="aso-dot-menu__danger"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="aso-dot-menu__icon">🗑</span> Delete Order
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════
   VIEW / DETAIL MODAL
   ═══════════════════════════════════ */
function ViewModal({ order, onClose }) {
  const [full,    setFull]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (!order) return;
    setLoading(true);
    apiFetch(`/api/admin/orders/${order.id}`)
      .then(setFull)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [order]);

  if (!order) return null;

  const data  = full || order;
  const items = full?.items || [];

  return (
    <div className="aso-modal-overlay" onClick={onClose}>
      <div className="aso-modal aso-modal--view" onClick={(e) => e.stopPropagation()}>
        <div className="aso-modal__header">
          <div className="aso-modal__title-row">
            <span className="aso-modal__tag">ORDER</span>
            <h2 className="aso-modal__title">{data.order_number}</h2>
          </div>
          <button className="aso-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="aso-modal__body aso-modal__body--view">
          {error && <div className="aso-modal__error">⚠ {error}</div>}

          {loading ? (
            <div className="aso-table-skeleton" style={{ height: 200 }} />
          ) : (
            <>
              {/* ── Order Info ── */}
              <div>
                <p className="aso-section-title">Order Information</p>
                <div className="aso-view-grid">
                  {[
                    ["Order #",        data.order_number],
                    ["Date",           fmtDateTime(data.created_at)],
                    ["Order Status",   <OrderStatusBadge status={data.status} />],
                    ["Payment",        <PaymentBadge status={data.payment_status} />],
                    ["Payment Method", data.payment_method || "—"],
                    ["Subtotal",       fmt(data.subtotal)],
                    ["Discount",       data.discount_amount > 0 ? `− ${fmt(data.discount_amount)}` : "—"],
                    ["Shipping",       data.shipping_cost > 0 ? fmt(data.shipping_cost) : "Free"],
                    ["Total",          fmt(data.total)],
                  ].map(([label, val]) => (
                    <div key={label} className="aso-view-row">
                      <span className="aso-view-row__label">{label}</span>
                      <span className="aso-view-row__val">{val}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Customer ── */}
              <div>
                <p className="aso-section-title">Customer</p>
                <div className="aso-view-grid">
                  {[
                    ["Name",  data.customer_name  || "—"],
                    ["Email", data.customer_email || "—"],
                    ["Phone", data.customer_phone || "—"],
                  ].map(([label, val]) => (
                    <div key={label} className="aso-view-row">
                      <span className="aso-view-row__label">{label}</span>
                      <span className="aso-view-row__val">{val}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Shipping Address ── */}
              {data.shipping_address && (
                <div>
                  <p className="aso-section-title">Shipping Address</p>
                  <div className="aso-address-block">{data.shipping_address}</div>
                </div>
              )}

              {/* ── Items ── */}
              <div>
                <p className="aso-section-title">Items ({items.length})</p>
                {items.length === 0 ? (
                  <div className="aso-empty" style={{ padding: "20px" }}>
                    <p>No items found for this order.</p>
                  </div>
                ) : (
                  <>
                    <div className="aso-items-table-wrap">
                      <table className="aso-items-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>SKU</th>
                            <th>Qty</th>
                            <th>Unit Price</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item) => (
                            <tr key={item.id}>
                              <td>
                                <div className="aso-items-table__name">{item.product_name}</div>
                              </td>
                              <td>
                                <div className="aso-items-table__sku">{item.sku || "—"}</div>
                              </td>
                              <td>{item.quantity}</td>
                              <td>{fmt(item.unit_price)}</td>
                              <td className="aso-items-table__total">{fmt(item.total_price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="aso-totals" style={{ marginTop: 8 }}>
                      <div className="aso-totals__row">
                        <span className="aso-totals__label">Subtotal</span>
                        <span className="aso-totals__val">{fmt(data.subtotal)}</span>
                      </div>
                      {Number(data.discount_amount) > 0 && (
                        <div className="aso-totals__row">
                          <span className="aso-totals__label">Discount</span>
                          <span className="aso-totals__val" style={{ color: "#15803d" }}>
                            − {fmt(data.discount_amount)}
                          </span>
                        </div>
                      )}
                      <div className="aso-totals__row">
                        <span className="aso-totals__label">Shipping</span>
                        <span className="aso-totals__val">
                          {Number(data.shipping_cost) > 0 ? fmt(data.shipping_cost) : "Free"}
                        </span>
                      </div>
                      <div className="aso-totals__row aso-totals__row--grand">
                        <span className="aso-totals__label">Total</span>
                        <span className="aso-totals__val">{fmt(data.total)}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* ── Notes ── */}
              {data.notes && (
                <div>
                  <p className="aso-section-title">Notes</p>
                  <div className="aso-notes-block">{data.notes}</div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="aso-modal__footer">
          <button className="aso-btn aso-btn--cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   UPDATE STATUS MODAL
   ═══════════════════════════════════ */
function UpdateStatusModal({ order, onClose, onSaved }) {
  const [orderStatus,   setOrderStatus]   = useState(order?.status          || "pending");
  const [paymentStatus, setPaymentStatus] = useState(order?.payment_status  || "unpaid");
  const [notes,         setNotes]         = useState(order?.notes           || "");
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState("");

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await apiFetch(`/api/admin/orders/${order.id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status:         orderStatus,
          payment_status: paymentStatus,
          notes,
        }),
      });
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aso-modal-overlay" onClick={onClose}>
      <div className="aso-modal aso-modal--update" onClick={(e) => e.stopPropagation()}>
        <div className="aso-modal__header">
          <div className="aso-modal__title-row">
            <span className="aso-modal__tag">UPDATE</span>
            <h2 className="aso-modal__title">{order?.order_number}</h2>
          </div>
          <button className="aso-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="aso-modal__body aso-modal__body--update">
          {error && <div className="aso-modal__error">⚠ {error}</div>}

          <div className="aso-form-field">
            <label>Order Status</label>
            <select value={orderStatus} onChange={(e) => setOrderStatus(e.target.value)}>
              {ORDER_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div className="aso-form-field">
            <label>Payment Status</label>
            <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
              {PAYMENT_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div className="aso-form-field">
            <label>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a note about this update…"
              rows={3}
            />
          </div>
        </div>

        <div className="aso-modal__footer">
          <button className="aso-btn aso-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="aso-btn aso-btn--save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   CONFIRM PAYMENT MODAL
   ═══════════════════════════════════ */
function ConfirmPaymentModal({ order, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  const handleConfirm = async () => {
    setSaving(true);
    setError("");
    try {
      await apiFetch(`/api/admin/orders/${order.id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status:         order.status === "pending" ? "confirmed" : order.status,
          payment_status: "paid",
        }),
      });
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aso-modal-overlay" onClick={onClose}>
      <div
        className="aso-modal aso-modal--update"
        style={{ maxWidth: 400 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aso-modal__header">
          <div className="aso-modal__title-row">
            <span className="aso-modal__tag">PAYMENT</span>
            <h2 className="aso-modal__title">Confirm Payment</h2>
          </div>
          <button className="aso-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="aso-modal__body aso-modal__body--update">
          {error && <div className="aso-modal__error">⚠ {error}</div>}
          <p style={{ margin: 0, fontSize: ".87rem", color: "#374151", lineHeight: 1.6 }}>
            Mark order <strong>{order?.order_number}</strong> as{" "}
            <strong style={{ color: "#15803d" }}>Paid</strong>?
            <br />
            Total: <strong>{fmt(order?.total)}</strong>
          </p>
          <p style={{ margin: 0, fontSize: ".81rem", color: "#6b7280" }}>
            This will set the payment status to <em>Paid</em> and confirm the order.
          </p>
        </div>

        <div className="aso-modal__footer">
          <button className="aso-btn aso-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="aso-btn--confirm-pay" onClick={handleConfirm} disabled={saving}>
            {saving ? "Confirming…" : "✅ Confirm Payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteOrderModal({ order, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await apiFetch(`/api/admin/orders/${order.id}`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="aso-modal-overlay" onClick={onClose}>
      <div
        className="aso-modal aso-modal--update"
        style={{ maxWidth: 420 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aso-modal__header">
          <div className="aso-modal__title-row">
            <span className="aso-modal__tag">DELETE</span>
            <h2 className="aso-modal__title">Delete Order</h2>
          </div>
          <button className="aso-modal__close" onClick={onClose}>✕</button>
        </div>
        <div className="aso-modal__body aso-modal__body--update">
          {error && <div className="aso-modal__error">⚠ {error}</div>}
          <p style={{ margin: 0, fontSize: ".87rem", color: "#374151", lineHeight: 1.6 }}>
            Permanently delete order <strong>{order?.order_number}</strong>?<br />
            This will also remove all <strong>order items</strong>. This cannot be undone.
          </p>
        </div>
        <div className="aso-modal__footer">
          <button className="aso-btn aso-btn--cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button
            className="aso-btn aso-btn--save"
            style={{ background: "#d62828" }}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "🗑 Delete Order"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminShopOrders() {
  const [orders,     setOrders]     = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  /* Filters */
  const [search,        setSearch]        = useState("");
  const [statusFilter,  setStatusFilter]  = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [sort,          setSort]          = useState("newest");
  const [page,          setPage]          = useState(1);
  const searchTimer = useRef(null);

  /* Modals */
  const [modal,  setModal]  = useState(null);
  const [target, setTarget] = useState(null);

  /* ── Load orders ── */
  const loadOrders = useCallback(() => {
    setLoading(true);
    setError("");
    const q = new URLSearchParams();
    if (search)        q.set("search",         search);
    if (statusFilter)  q.set("status",          statusFilter);
    if (paymentFilter) q.set("payment_status",  paymentFilter);
    q.set("sort",  sort);
    q.set("page",  page);
    q.set("limit", "15");

    apiFetch(`/api/admin/orders/list?${q}`)
      .then((data) => {
        setOrders(data.orders     || []);
        setPagination(data.pagination || { page: 1, total: 0, pages: 1 });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, statusFilter, paymentFilter, sort, page]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  /* ── Handlers ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setPage(1), 400);
  };

  const openModal  = (type, order = null) => { setModal(type); setTarget(order); };
  const closeModal = () => { setModal(null); setTarget(null); };
  const afterSave  = () => { closeModal(); loadOrders(); };

  /* ── Inline quick-confirm payment ── */
  const quickConfirmPayment = async (order) => {
    try {
      await apiFetch(`/api/admin/orders/${order.id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status:         order.status === "pending" ? "confirmed" : order.status,
          payment_status: "paid",
        }),
      });
      loadOrders();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <AdminLayout pageTag="SHOP" pageTitle="Orders">

      {error && <div className="aso-error-bar">⚠ {error}</div>}

      {/* ── Toolbar ── */}
      <div className="aso-toolbar">
        <div className="aso-toolbar__left">
          <div className="aso-search-wrap">
            <span className="aso-search-wrap__icon">🔍</span>
            <input
              className="aso-search"
              type="text"
              placeholder="Search order #, customer name, email…"
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <button
                className="aso-search-wrap__clear"
                onClick={() => { setSearch(""); setPage(1); }}
              >✕</button>
            )}
          </div>

          <select
            className="aso-filter-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Statuses</option>
            {ORDER_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          <select
            className="aso-filter-select"
            value={paymentFilter}
            onChange={(e) => { setPaymentFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Payments</option>
            {PAYMENT_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          <select
            className="aso-filter-select"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="total_desc">Total ↓</option>
            <option value="total_asc">Total ↑</option>
          </select>
        </div>

        <div className="aso-toolbar__right">
          <span className="aso-toolbar__count">
            {loading ? "…" : `${pagination.total} order${pagination.total !== 1 ? "s" : ""}`}
          </span>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="aso-table-skeleton" />
      ) : orders.length === 0 ? (
        <div className="aso-empty">
          <span>🗒</span>
          <p>No orders found.</p>
          {(search || statusFilter || paymentFilter) && (
            <button
              className="aso-btn aso-btn--cancel"
              onClick={() => { setSearch(""); setStatusFilter(""); setPaymentFilter(""); setPage(1); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="aso-table-wrap">
          <table className="aso-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Method</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td>
                    <div className="aso-order-cell">
                      <span className="aso-order-cell__num">{o.order_number}</span>
                      <span className="aso-order-cell__date">{fmtDate(o.created_at)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="aso-customer-cell">
                      <span className="aso-customer-cell__name">{o.customer_name || "—"}</span>
                      {o.customer_email && (
                        <span className="aso-customer-cell__email">{o.customer_email}</span>
                      )}
                      {o.customer_phone && (
                        <span className="aso-customer-cell__phone">{o.customer_phone}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="aso-items-summary">
                      {o.item_count != null
                        ? `${o.item_count} item${o.item_count !== 1 ? "s" : ""}`
                        : "—"}
                    </div>
                  </td>
                  <td>
                    <span className="aso-total">{fmt(o.total)}</span>
                  </td>
                  <td>
                    <OrderStatusBadge status={o.status} />
                  </td>
                  <td>
                    <PaymentBadge status={o.payment_status} />
                  </td>
                  <td style={{ fontSize: ".81rem", color: "#6b7280" }}>
                    {o.payment_method || "—"}
                  </td>
                  <td>
                    <DotMenu
                        onView={()            => openModal("view",    o)}
                        onUpdateStatus={()    => openModal("update",  o)}
                        onConfirmPayment={() => openModal("payment", o)}
                        onDelete={()          => openModal("delete",  o)}
                        isPaid={o.payment_status === "paid"}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {pagination.pages > 1 && (
        <div className="aso-pagination">
          <button
            className="aso-pagination__btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >← Prev</button>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`aso-pagination__btn ${p === page ? "active" : ""}`}
              onClick={() => setPage(p)}
            >{p}</button>
          ))}
          <button
            className="aso-pagination__btn"
            disabled={page >= pagination.pages}
            onClick={() => setPage((p) => p + 1)}
          >Next →</button>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "view"    && <ViewModal           order={target} onClose={closeModal} />}
      {modal === "update"  && <UpdateStatusModal   order={target} onClose={closeModal} onSaved={afterSave} />}
      {modal === "payment" && <ConfirmPaymentModal  order={target} onClose={closeModal} onSaved={afterSave} />}
      {modal === "delete" && (  <DeleteOrderModal order={target} onClose={closeModal} onDeleted={afterSave} />)}
    </AdminLayout>
  );
}